import { QueryClient, QueryClientProvider, isCancelledError, useInfiniteQuery, useQuery, type QueryKey } from "@tanstack/react-query";
import { subscribeApiMutations } from "@pustak-lms/api-client";
import type { SessionIdentity } from "@pustak-lms/contracts";
import type { ReactNode } from "react";

export const PAGE_STALE_TIME = 60_000;
export const REFERENCE_STALE_TIME = 300_000;
export const appQueryClient = new QueryClient({ defaultOptions: {
  queries: { staleTime: PAGE_STALE_TIME, gcTime: 30 * 60_000, retry: false, refetchOnWindowFocus: false },
  mutations: { retry: false }
} });
let scope = "anonymous";
let cacheGeneration = 0;
const root = () => ["pustak", typeof window === "undefined" ? "server" : window.location.origin, scope] as const;
export const cachedQueryKey = (key: QueryKey) => [...root(), ...key];

export function resetQueryCache() {
  // clear destroys observers' old queries and cancels in-flight retryers, so late
  // promises cannot seed the next identity's cache.
  ++cacheGeneration;
  appQueryClient.clear();
  scope = "anonymous";
}
export function setQueryIdentity(identity: SessionIdentity | null, mode: string) {
  const next = identity ? JSON.stringify([mode, identity.user.id, identity.user.role, identity.organization?.id,
    identity.membership?.memberType, identity.membership?.status]) : "anonymous";
  if (next !== scope) { resetQueryCache(); scope = next; }
}
subscribeApiMutations(() => {
  // A GET started before the write may contain an older snapshot, even when
  // this is its first load. Cancel it before scheduling current reads.
  void appQueryClient.cancelQueries({ queryKey: ["pustak"] });
  void appQueryClient.invalidateQueries({ queryKey: ["pustak"], refetchType: "none" });
  // Opted-out lists reconcile loaded rows themselves, but a cancelled first
  // load has no rows to reconcile and must restart.
  void appQueryClient.refetchQueries({ type: "active", predicate: query => query.queryKey[0] === "pustak" && (query.meta?.mutationRefetch !== false || query.state.data === undefined) });
});

export function AppQueryProvider({ children }: { children: ReactNode }) {
  return <QueryClientProvider client={appQueryClient}>{children}</QueryClientProvider>;
}
type CacheOptions = { enabled?: boolean; staleTime?: number; mutationRefetch?: boolean };
export function useCachedQuery<T>(key: QueryKey, load: () => Promise<T>, options: CacheOptions = {}) {
  const queryKey = cachedQueryKey(key);
  const generation = cacheGeneration;
  const { mutationRefetch = true, staleTime = PAGE_STALE_TIME, ...queryOptions } = options;
  const query = useQuery({ queryKey, queryFn: load, staleTime, meta: { mutationRefetch }, ...queryOptions }, appQueryClient);
  return { ...query, setData: (updater: T | ((previous: T | undefined) => T | undefined)) => {
    if (generation === cacheGeneration) return appQueryClient.setQueryData<T>(queryKey, updater);
  } };
}
/** Editor initialization: reuse data without subscribing local forms to refetches. */
export async function fetchCachedQuery<T>(key: QueryKey, load: () => Promise<T>, options: { staleTime?: number; force?: boolean } = {}) {
  const generation = cacheGeneration;
  const queryKey = cachedQueryKey(key);
  while (true) {
    try {
      return await appQueryClient.fetchQuery({ queryKey, queryFn: load,
        staleTime: options.force ? 0 : options.staleTime ?? PAGE_STALE_TIME });
    } catch (error) {
      // A committed write restarts subscribed readers automatically. Imperative
      // editor initialization has no observer, so join/start the current read.
      // A reset/sign-out cancels permanently; never retry into another account.
      if (!isCancelledError(error) || generation !== cacheGeneration) throw error;
    }
  }
}
export function createCachedQueryWriter() {
  const generation = cacheGeneration;
  return <T,>(key: QueryKey, data: T) => {
    if (generation !== cacheGeneration) return;
    const queryKey = cachedQueryKey(key);
    if (appQueryClient.getQueryData(queryKey) !== data) appQueryClient.setQueryData(queryKey, data);
  };
}
export function useCachedInfiniteQuery<T, P>(key: QueryKey, load: (page: P) => Promise<T>, initialPageParam: P,
  getNextPageParam: (page: T, pages: T[]) => P | undefined, options: CacheOptions = {}) {
  const { mutationRefetch = true, ...queryOptions } = options;
  return useInfiniteQuery({ queryKey: cachedQueryKey(key), queryFn: ({ pageParam }) => load(pageParam as P),
    initialPageParam, getNextPageParam, meta: { mutationRefetch }, ...queryOptions }, appQueryClient);
}
